import { Button, Divider, Stack, TextField, Typography } from "@mui/material";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import DialogHeader from "@entrilia/shared/components/dialog/DialogHeader";
import { selectFieldsForCalculation } from "../../../../../store/selectors/fieldsStateSelectors";
import { selectDimensions } from "../../../../../store/selectors/metaDataSelectors";
import { DimensionNames } from "../../../../../utilities/DimensionNames";
import { isEligibleDateScopeField } from "../../../../../utilities/fieldsHelper";
import DateToShowSelect from "./controls/DateToShowSelect";
import DateTypeSelect from "./controls/DateTypeSelect";
import ReferenceCalculatedFieldSelect from "./controls/ReferenceCalculatedFIeldSelect";
import type { DateCalculationData } from "./customColumn.types";
import useSetupDateCalculationMenu from "./hooks/useSetupDateCalculationMenu";
import type { DateCalculationType } from "./utilities/dateCalculationTypes";

interface Props {
  dateCalculationType: DateCalculationType;
  fieldGuid: string | undefined;
  onClose: () => void;
  onApply: (data: DateCalculationData) => void;
  readonly: boolean;
}

const dateFieldNames: string[] = [DimensionNames.PostingDate, DimensionNames.TransactionDate];

const CustomColumnDateCalculationSetup = ({ dateCalculationType, fieldGuid, onClose, onApply, readonly }: Props) => {
  const allFieldsForCalculation = useSelector(selectFieldsForCalculation);
  const dimensions = useSelector(selectDimensions);

  const eligibleFields = useMemo(
    () => allFieldsForCalculation.filter(isEligibleDateScopeField),
    [allFieldsForCalculation]
  );
  const dateFields = useMemo(
    () =>
      dateFieldNames
        .map((name) => dimensions.find((d) => d.name === name))
        .filter((d): d is NonNullable<typeof d> => d != null)
        .map((d) => ({ name: d.name, caption: d.caption })),
    [dimensions]
  );

  const {
    isValid,
    isDirty,
    name,
    nameError,
    selectedType,
    selectedFormulaParameterIds,
    selectedDateField,
    onNameChange,
    onSelectedTypeChange,
    onSelectedFormulaParameterIdsChange,
    onSelectedDateFieldChange,
  } = useSetupDateCalculationMenu(dateCalculationType, fieldGuid, eligibleFields, dateFields);

  const handleApply = () => {
    const dependsFromFieldId = selectedFormulaParameterIds[0];
    if (!selectedDateField || !dependsFromFieldId) {
      return;
    }
    onApply({ type: selectedType, dateField: selectedDateField, dependsFromFieldId, name });
  };

  const hasOptions = eligibleFields.length > 0 && dateFields.length > 0;

  return (
    <Stack>
      <DialogHeader text="New Calculation" onClose={onClose} />
      <Divider />
      {hasOptions && (
        <Stack spacing={2} sx={{ p: 2.5 }}>
          <DateTypeSelect selectedType={selectedType} onSelectedTypeChange={onSelectedTypeChange} readonly={readonly} />
          <DateToShowSelect
            selectedDateField={selectedDateField}
            onSelectedDateFieldChange={onSelectedDateFieldChange}
            dateFields={dateFields}
            readonly={readonly}
          />
          <ReferenceCalculatedFieldSelect
            selectedFieldGuid={selectedFormulaParameterIds[0] ?? ""}
            onSelectedFieldGuidChange={(guid) => onSelectedFormulaParameterIdsChange([guid])}
            calculatedFields={eligibleFields}
            selectedFieldGuidError={undefined}
            readonly={readonly}
          />
          <TextField
            autoFocus={!isDirty && isValid}
            label="Name"
            value={name}
            onChange={(evt) => onNameChange(evt.target.value)}
            error={!!nameError}
            helperText={nameError}
          />
        </Stack>
      )}
      {!hasOptions && (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
          No measures available for a date calculation
        </Typography>
      )}
      <Divider />
      <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", px: 2, py: 1.5 }}>
        <Button variant="text" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!isValid}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};

export default CustomColumnDateCalculationSetup;
